import timezoneCountries from "./timezone-countries.json";
import type { SelectOption } from "@/modules/auth/services/auth.api";
import { usTimezoneName } from "./usTimezones";

/**
 * "Country / Time Zone" is one control for the user but two values for the API
 * (an ISO 3166-1 alpha-2 country and an IANA zone), and nothing in the
 * database links them. `timezone-countries.json` is generated from the IANA
 * zone table - see scripts/generate-timezone-countries.mjs - and is the join.
 */
const TIMEZONE_TO_COUNTRY = timezoneCountries as Record<string, string>;

/** The country an IANA zone belongs to, or null for a zone that is not a place. */
export function countryForTimezone(timezone: string): string | null {
  return TIMEZONE_TO_COUNTRY[timezone] ?? null;
}

/** One time zone of a country, split for display. */
export type CountryTimezoneZone = {
  /** The IANA zone - what the API stores. */
  value: string;
  /** "(UTC-07:00)", as the API formatted it today (it follows DST). */
  offset: string;
  /** "Pacific Time" for the six US zones, else the IANA city ("Buenos Aires"). */
  place: string;
};

export type CountryTimezoneGroup = {
  code: string;
  name: string;
  zones: CountryTimezoneZone[];
};

/**
 * The place an IANA zone is named after: its last segment, underscores as
 * spaces - "America/Argentina/Buenos_Aires" gives "Buenos Aires".
 */
function placeForTimezone(timezone: string): string {
  return usTimezoneName(timezone) ?? (timezone.split("/").pop() ?? timezone).replace(/_/g, " ");
}

/**
 * Every country the IANA zone table knows, each with its time zones, for the
 * registration page's country picker: a country with one zone is a single
 * choice, a country with several opens them as a sub-menu.
 *
 * A zone must be in the API's timezone list and its country in the API's
 * country list, or the backend would reject it. Zones with no country are
 * dropped - in practice only "UTC", the column default a Google-created
 * account starts with and exactly what this field replaces. Countries are sorted by
 * their (localised) name; zones keep the API's order, which is by UTC offset,
 * so a sub-menu reads west to east.
 */
export function buildCountryTimezoneGroups(
  countries: SelectOption[],
  timezones: SelectOption[],
): CountryTimezoneGroup[] {
  const countryNames = new Map(countries.map((c) => [c.value.toUpperCase(), c.label]));
  const groups = new Map<string, CountryTimezoneGroup>();

  for (const timezone of timezones) {
    const code = countryForTimezone(timezone.value)?.toUpperCase();
    const name = code ? countryNames.get(code) : undefined;

    if (!code || !name) {
      continue;
    }

    const group = groups.get(code) ?? { code, name, zones: [] };
    group.zones.push({
      value: timezone.value,
      offset: timezone.label.match(/^\(UTC[^)]*\)/)?.[0] ?? "",
      place: placeForTimezone(timezone.value),
    });
    groups.set(code, group);
  }

  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The time zone to assume for a country picked somewhere else (e.g. the phone
 * number's flag): its zone when it has exactly one, otherwise null - a country
 * with several cannot be guessed, so the user picks from its sub-menu.
 */
export function defaultTimezoneForCountry(
  groups: CountryTimezoneGroup[],
  countryCode: string,
): string | null {
  const group = groups.find((candidate) => candidate.code === countryCode.toUpperCase());

  return group && group.zones.length === 1 ? group.zones[0].value : null;
}

/**
 * Whether `timezone` is one the Country / Time Zone picker offers. A saved
 * value that is not (e.g. the "UTC" column default) shows as empty there, so
 * forms treat it as unanswered rather than silently re-saving it.
 */
export function isOfferedTimezone(groups: CountryTimezoneGroup[], timezone: string): boolean {
  return groups.some((group) => group.zones.some((zone) => zone.value === timezone));
}
